import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    DatePicker,
    Flex,
    Form,
    InputNumber,
    Popconfirm,
    Segmented,
    Space,
    Table,
    Tag,
    message,
} from "antd";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RTooltip,
    XAxis,
    YAxis,
} from "recharts";
import dayjs from "dayjs";
import DailyMonitoringService from "../../../../services/DailyMonitoringService";

const SOURCE_LABELS = {
    WATER: "Талас",
    KORDAI: "Кордай",
};

const COLOR_MANUAL = "#0039C7";
const COLOR_COMPUTED = "#FF8C00";

const diffStyle = (diff) => ({
    color: diff >= 0 ? "#2f9e44" : "#d63939",
    fontWeight: 600,
});

const DailyMonitoring = () => {
    const [source, setSource] = useState("WATER");
    const [month, setMonth] = useState(dayjs().startOf("month"));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await DailyMonitoringService.adminList({
                source,
                from: month.startOf("month").format("YYYY-MM-DD"),
                to: month.endOf("month").format("YYYY-MM-DD"),
            });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            message.error("Не удалось загрузить дневную статистику");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [source, month]);

    useEffect(() => {
        load();
    }, [load]);

    const onSubmitManual = async (values) => {
        setSubmitting(true);
        try {
            await DailyMonitoringService.upsertManual({
                source,
                date: values.date.format("YYYY-MM-DD"),
                manualTotalFlow: values.manualTotalFlow,
            });
            message.success("Ручной ввод сохранён");
            form.resetFields();
            await load();
        } catch {
            message.error("Не удалось сохранить ручной ввод");
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (id) => {
        try {
            await DailyMonitoringService.deleteManual(id);
            message.success("Ручной ввод удалён");
            await load();
        } catch {
            message.error("Не удалось удалить");
        }
    };

    const monthRows = useMemo(() => {
        const monthKey = month.format("YYYY-MM");
        return rows.filter((r) => r.date && r.date.startsWith(monthKey));
    }, [rows, month]);

    const chartData = useMemo(() => {
        const byDate = new Map(rows.map((r) => [r.date, r]));
        const start = month.startOf("month");
        const daysInMonth = start.daysInMonth();
        const out = [];
        for (let d = 0; d < daysInMonth; d++) {
            const day = start.add(d, "day").format("YYYY-MM-DD");
            const r = byDate.get(day);
            out.push({
                date: day,
                manual: r ? r.manualTotalFlow ?? null : null,
                computed: r ? r.computedAvgFlow ?? null : null,
            });
        }
        return out;
    }, [rows, month]);

    const summary = useMemo(() => {
        const withBoth = rows.filter(
            (r) =>
                r.manualTotalFlow !== null &&
                r.manualTotalFlow !== undefined &&
                r.computedAvgFlow !== null &&
                r.computedAvgFlow !== undefined
        );
        const manualOnly = rows.filter(
            (r) => r.manualTotalFlow !== null && r.manualTotalFlow !== undefined
        ).length;
        const computedOnly = rows.filter(
            (r) => r.computedAvgFlow !== null && r.computedAvgFlow !== undefined
        ).length;
        const avgDiff =
            withBoth.length > 0
                ? withBoth.reduce(
                      (acc, r) => acc + (r.manualTotalFlow - r.computedAvgFlow),
                      0
                  ) / withBoth.length
                : null;
        return { manualOnly, computedOnly, withBoth: withBoth.length, avgDiff };
    }, [rows]);

    const columns = [
        {
            title: "Дата",
            dataIndex: "date",
            key: "date",
            width: 110,
            sorter: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
            render: (v) => dayjs(v).format("DD.MM.YYYY"),
        },
        {
            title: "Ручной ввод",
            dataIndex: "manualTotalFlow",
            key: "manualTotalFlow",
            width: 130,
            render: (v) =>
                v !== null && v !== undefined ? (
                    <b style={{ color: COLOR_MANUAL }}>{Number(v).toFixed(3)}</b>
                ) : (
                    <span style={{ color: "#bbb" }}>—</span>
                ),
        },
        {
            title: "Средняя скорость (сенсоры)",
            dataIndex: "computedAvgFlow",
            key: "computedAvgFlow",
            width: 200,
            render: (v) =>
                v !== null && v !== undefined ? (
                    <span style={{ color: COLOR_COMPUTED }}>{Number(v).toFixed(3)}</span>
                ) : (
                    <span style={{ color: "#bbb" }}>—</span>
                ),
        },
        {
            title: "Записей",
            dataIndex: "computedRecordCount",
            key: "computedRecordCount",
            width: 90,
            render: (v) => v ?? 0,
        },
        {
            title: "Δ (ручной − среднее)",
            key: "diff",
            width: 180,
            render: (_, row) => {
                if (
                    row.manualTotalFlow === null ||
                    row.manualTotalFlow === undefined ||
                    row.computedAvgFlow === null ||
                    row.computedAvgFlow === undefined
                ) {
                    return <span style={{ color: "#6F8695" }}>—</span>;
                }
                const diff = row.manualTotalFlow - row.computedAvgFlow;
                const pct =
                    row.computedAvgFlow !== 0
                        ? ((diff / row.computedAvgFlow) * 100).toFixed(1)
                        : "∞";
                return (
                    <span style={diffStyle(diff)}>
                        {diff >= 0 ? "+" : ""}
                        {diff.toFixed(3)} ({pct}%)
                    </span>
                );
            },
        },
        {
            title: "",
            key: "actions",
            width: 120,
            fixed: "right",
            render: (_, row) => (
                <Popconfirm
                    title="Удалить ручной ввод?"
                    onConfirm={() => onDelete(row.id)}
                    okText="Да"
                    cancelText="Отмена"
                    disabled={row.manualTotalFlow === null || row.manualTotalFlow === undefined || !row.id}
                >
                    <Button
                        size="small"
                        danger
                        disabled={row.manualTotalFlow === null || row.manualTotalFlow === undefined || !row.id}
                    >
                        Удалить
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Flex vertical gap={16} style={{ width: "100%" }}>
            <Card size="small" bordered>
                <Flex wrap="wrap" gap={16} align="center">
                    <Segmented
                        options={[
                            { label: SOURCE_LABELS.WATER, value: "WATER" },
                            { label: SOURCE_LABELS.KORDAI, value: "KORDAI" },
                        ]}
                        value={source}
                        onChange={setSource}
                    />
                    <DatePicker
                        picker="month"
                        value={month}
                        onChange={(v) => setMonth(v || dayjs().startOf("month"))}
                        allowClear={false}
                        format="MMMM YYYY"
                    />
                    <Button onClick={load} loading={loading}>
                        Обновить
                    </Button>
                    <Space size={8} style={{ marginLeft: "auto" }} wrap>
                        <Tag color="blue">Ручной ввод: {summary.manualOnly}</Tag>
                        <Tag color="orange">Сенсоры: {summary.computedOnly}</Tag>
                        <Tag color="purple">Совпадают: {summary.withBoth}</Tag>
                        {summary.avgDiff !== null && (
                            <Tag color={summary.avgDiff >= 0 ? "green" : "red"}>
                                Δ ср.: {summary.avgDiff >= 0 ? "+" : ""}
                                {summary.avgDiff.toFixed(3)}
                            </Tag>
                        )}
                    </Space>
                </Flex>
            </Card>

            <Card
                size="small"
                title="Добавить / обновить ручной ввод"
                styles={{ header: { color: "#0039C7", fontWeight: 700 } }}
            >
                <Form
                    form={form}
                    layout="inline"
                    onFinish={onSubmitManual}
                    initialValues={{ date: dayjs() }}
                >
                    <Form.Item
                        label="Дата"
                        name="date"
                        rules={[{ required: true, message: "Выберите дату" }]}
                    >
                        <DatePicker format="DD.MM.YYYY" />
                    </Form.Item>
                    <Form.Item
                        label="Скорость, м/с"
                        name="manualTotalFlow"
                        rules={[
                            { required: true, message: "Введите значение" },
                            { type: "number", min: 0, message: "Не может быть отрицательной" },
                        ]}
                    >
                        <InputNumber step={0.001} min={0} style={{ width: 180 }} placeholder="Напр. 1.234" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={submitting}>
                            Сохранить
                        </Button>
                    </Form.Item>
                </Form>
                <div style={{ marginTop: 8, color: "#6F8695", fontSize: "0.9rem" }}>
                    Источник: <b>{SOURCE_LABELS[source]}</b>. Если запись на эту дату уже существует — она будет обновлена.
                </div>
            </Card>

            <Card
                size="small"
                title="График скорости: ручной ввод vs среднее по сенсорам"
                styles={{ header: { color: "#0039C7", fontWeight: 700 } }}
            >
                <div style={{ width: "100%", height: 360 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(v) => dayjs(v).format("DD.MM")}
                                stroke="#6F8695"
                            />
                            <YAxis stroke="#6F8695" />
                            <RTooltip
                                labelFormatter={(v) => dayjs(v).format("DD.MM.YYYY")}
                                formatter={(value, name) => [
                                    value !== null && value !== undefined ? Number(value).toFixed(3) : "—",
                                    name === "manual" ? "Ручной ввод" : "Среднее (сенсоры)",
                                ]}
                            />
                            <Legend
                                formatter={(value) => (value === "manual" ? "Ручной ввод" : "Среднее (сенсоры)")}
                            />
                            <Line
                                type="monotone"
                                dataKey="manual"
                                stroke={COLOR_MANUAL}
                                strokeWidth={2.5}
                                dot={{ r: 4 }}
                                connectNulls
                            />
                            <Line
                                type="monotone"
                                dataKey="computed"
                                stroke={COLOR_COMPUTED}
                                strokeWidth={2.5}
                                dot={{ r: 3 }}
                                connectNulls
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card
                size="small"
                title="История по дням"
                styles={{ header: { color: "#0039C7", fontWeight: 700 } }}
            >
                <Table
                    columns={columns}
                    dataSource={monthRows}
                    loading={loading}
                    rowKey="id"
                    size="middle"
                    scroll={{ x: 1100 }}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                />
            </Card>
        </Flex>
    );
};

export default DailyMonitoring;
